// Relative imports only (no vite aliases): this module is imported by
// bun tests, which cannot resolve the client's runtime path aliases.
import type { DeliveryStats, DeliveryStatsDay } from '../types/dashboard'

/**
 * Merge the aggressively-cached settled history with the always-fresh live
 * slice, deduped by date. The live slice wins on overlap — it is the newer
 * fetch, and the windows deliberately share their boundary day so there is
 * never a gap between them.
 */
export function mergeDeliveryDays(history: DeliveryStatsDay[], live: DeliveryStatsDay[]): DeliveryStatsDay[] {
  const byDate = new Map<string, DeliveryStatsDay>()
  for (const day of history) {
    byDate.set(day.date, day)
  }
  for (const day of live) {
    byDate.set(day.date, day)
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Roll day buckets up into the totals/rates the delivery widget renders.
 * Same math the server's DashboardController used before the split.
 */
export function aggregateDeliveryStats(
  days: DeliveryStatsDay[],
  window: { periodDays: number; startDate: string },
): DeliveryStats {
  const totals = {
    requests: 0,
    delivered: 0,
    bounces: 0,
    blocks: 0,
    invalidEmails: 0,
    spamReports: 0,
    opens: 0,
    uniqueOpens: 0,
    clicks: 0,
    uniqueClicks: 0,
    unsubscribes: 0,
  }
  for (const day of days) {
    totals.requests += day.requests
    totals.delivered += day.delivered
    totals.bounces += day.bounces
    totals.blocks += day.blocks
    totals.invalidEmails += day.invalidEmails
    totals.spamReports += day.spamReports
    totals.opens += day.opens
    totals.uniqueOpens += day.uniqueOpens
    totals.clicks += day.clicks
    totals.uniqueClicks += day.uniqueClicks
    totals.unsubscribes += day.unsubscribes
  }

  const safeDivide = (n: number, d: number) => (d > 0 ? n / d : 0)
  return {
    periodDays: window.periodDays,
    startDate: window.startDate,
    ...totals,
    deliveryRate: safeDivide(totals.delivered, totals.requests),
    bounceRate: safeDivide(totals.bounces + totals.blocks + totals.invalidEmails, totals.requests),
    openRate: safeDivide(totals.uniqueOpens, totals.delivered),
    clickRate: safeDivide(totals.uniqueClicks, totals.delivered),
  }
}
